/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  SingleValueParameter,
  MultiValueParameter,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { CubeDefinition, OLAPResult } from "@sa/rep-olap";
import { SelectionMode, AggregationType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, ServicesRegister } from "../../shared";
import { debounceTime, shareReplay, tap } from "rxjs/operators";
import { arrayByLength, clone, equal } from "../../../../shared";
import { SLIDE_NUMBER } from "./constants";
import { DictionariesService, DictionaryItem } from "@sa/rep-mdm";
import { getCountriesSelection } from './utils/getCountriesSelection';
import { getDefaultCalendarSelection } from './utils/getDefaultCalendarSelection';

interface Parameters {
  businessUnits: number;
  calendar: number;
  countries: number[];
  commodities: number;
  countriesDisplay: number[];
  commoditiesDisplay: number;
  attributes: number[];
};

type HighlightSelection = Record<string, number>;

export class SlideContext implements IComponentContext {
  private readonly _cubeDefPromise: Promise<CubeDefinition> | undefined;

  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;

  private _highlight$ = new BehaviorSubject<HighlightSelection | null>(null);
  public get highlight$() {
    return this._highlight$.pipe(
      debounceTime(60),
      shareReplay(1)
    );
  }

  public set highlight(value: HighlightSelection | null) {
    this._highlight$.next(value);
  }
  public olapResultChart$ = new BehaviorSubject<OLAPResult | null>(null);
  public olapResultTable$ = new BehaviorSubject<OLAPResult | null>(null);
  public sources$ = new BehaviorSubject<string | null>(null);

  protected _parameters: BehaviorSubject<Parameters | undefined> = new BehaviorSubject<Parameters | undefined>(undefined);
  public get parameters$(): Observable<Parameters | undefined> {
    return this._parameters.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    this._cubeDefPromise = dataService.getCubeDefinition("slide_24");
    this._initDefaultMark();

    this._parameters
      .pipe(
        tap(() => {
          this.readyState = ComponentContextReadyState.Loading;
          this.olapResultChart$.next(null);
          this.olapResultTable$.next(null);
          this.sources$.next(null);
        }),
        debounceTime(600)
      )
      .subscribe(async (parameters) => {
        if (parameters) {
          const { dataflowService, serviceProvider } = this.options;
          const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);
          const { calendar: calendarId, commodities: commodityId, businessUnits: businessUnitsId } = parameters;
          const { countries, countriesDisplay } = await getCountriesSelection(calendarId, commodityId, businessUnitsId, dictionariesService);
          const countriesParameter = dataflowService.getParameter("@countries") as MultiValueParameter;
          const countriesDisplayParameter = dataflowService.getParameter("@countries_display") as MultiValueParameter;

          countriesParameter.value = parameters.countries = countries;
          countriesDisplayParameter.value = parameters.countriesDisplay = countriesDisplay as number[];

          this.query(parameters);
        }
      });
  }

  private async _initDefaultMark() {
    const { dataflowService, serviceProvider } = this.options;
    const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);

    const businessUnitsParameter = dataflowService.getParameter("@business-units") as SingleValueParameter;
    const lastYearParameter = dataflowService.getParameter("@last_year") as SingleValueParameter;
    const calendarParameter = dataflowService.getParameter("@calendar") as SingleValueParameter;
    const commoditiesParameter = dataflowService.getParameter("@commodities") as SingleValueParameter;
    const commoditiesDisplayParameter = dataflowService.getParameter("@commodities_display") as SingleValueParameter;
    const countriesParameter = dataflowService.getParameter("@countries") as MultiValueParameter;
    const countriesDisplayParameter = dataflowService.getParameter("@countries_display") as MultiValueParameter;
    const attributesParameter = dataflowService.getParameter("@attributes") as MultiValueParameter;

    const businessUnitsId = businessUnitsParameter.value as number;
    const commodityId = commoditiesParameter.value as number;
    const attributesIds = attributesParameter.value as number[];
    const calendarId = await getDefaultCalendarSelection(commodityId, attributesIds, dictionariesService);
    const { countries, countriesDisplay } = await getCountriesSelection(calendarId, commodityId, businessUnitsId, dictionariesService);

    calendarParameter.value = lastYearParameter.value = calendarId;

    countriesParameter.value = countries;
    countriesDisplayParameter.value = countriesDisplay;

    this._parameters.next({
      businessUnits: businessUnitsParameter.value as number,
      calendar: calendarParameter.value as number,
      commodities: commoditiesParameter.value as number,
      commoditiesDisplay: commoditiesDisplayParameter.value as number,
      countries: countriesParameter.value as number[],
      countriesDisplay: countriesDisplayParameter.value as number[],
      attributes: attributesIds,
    });

    this.dataflowIntegration.integrated = true;
  }

  async update(command: UpdateCommand): Promise<void> {
    const businessUnits = command.values.get("@business-units") as number;
    const calendar = command.values.get("@calendar") as number;
    const commodities = command.values.get("@commodities") as number;

    const parameters = clone(this._parameters.value);
    if (parameters !== undefined) {
      if (calendar !== undefined) {
        parameters.calendar = calendar;
      }
      if (commodities !== undefined) {
        parameters.commodities = commodities;
      }
      if (businessUnits !== undefined) {
        parameters.businessUnits = businessUnits;
      }
      if (!equal(parameters, this._parameters.value)) {
        this._parameters.next(parameters);
      }
    }
  }

  async query(parameters: Parameters) {
    const { dataSourcesService } = this.options;
    const { businessUnits, commodities } = parameters;
    const sources = await dataSourcesService.getDataSources(SLIDE_NUMBER, businessUnits, commodities);

    const [chartAttribute, tableAttribute] = [60, 30];
    const olapResultChart = await this._olapQuery({ ...parameters, attributes: [chartAttribute] });
    const olapResultTable = await this._olapQuery({ ...parameters, attributes: [tableAttribute] });

    this.olapResultChart$.next(olapResultChart);
    this.olapResultTable$.next(olapResultTable);
    this.readyState = ComponentContextReadyState.Loaded;
    this.sources$.next(sources);
  }

  private async _olapQuery(parameters: Parameters) {
    const { countries, calendar, attributes, businessUnits, commodities } = parameters;
    const calendarStep = 10000;
    const calendarSelection = [-5, -4, -3, -2, -1, 0].map(x => calendar + (x * calendarStep));

    const cubeSlideDef = await this._cubeDefPromise!;
    const cubeParameters = clone(cubeSlideDef.parameters);
    cubeParameters["p_commodity"]!["defaultValue"] = commodities;
    cubeParameters["p_countries"]!["defaultValue"] = countries.join(',');
    cubeParameters["p_attributes"]!["defaultValue"] = attributes.join(',');
    cubeParameters["p_calendar"]!["defaultValue"] = calendarSelection.join(',');
    cubeParameters["p_business_unit"]!["defaultValue"] = businessUnits;

    const cubeSlide = await dataService.getCube({
      id: cubeSlideDef.id,
      parameters: cubeParameters,
    });

    const olapQuery = ({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "countries.default",
            selection: countries,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      filters: {
        showEmpty: false,
        parts: [
          { path: "commodities.default", selection: [commodities], selectionMode: SelectionMode.Include },
          { path: "attributes.default", selection: attributes, selectionMode: SelectionMode.Include },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "calendar.default", selection: calendarSelection, selectionMode: SelectionMode.Include },
          {
            path: "Measures",
            measures: [
              { id: "value", aggregationType: AggregationType.None },
            ],
          },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any);

    const olapResult = await cubeSlide.query(olapQuery);
    return olapResult;
  }
}
